import { createContext } from 'react';
import { noop } from '@philips-internal/oswald-ui-utils';
/**
 * Holds data as a tree, allowing current and all parent levels to be accessed.
 */
class DataContext {
    /**
     * @param data - The data to pre-fill this context with.
     * @param parent - The parent DataContext.
     */
    constructor(data = {}, parent) {
        this.listeners = new Set();
        this.unlisten = noop;
        /**
         * Fires all listeners with the current data as their argument.
         */
        this.fireListeners = () => {
            const data = this.getBulkData();
            for (const listener of this.listeners) {
                listener(data);
            }
        };
        this.data = data;
        this.parent = parent;
        if (parent) {
            this.unlisten = parent.addListener(this.fireListeners);
        }
    }
    /**
     * Add a listener to be notified of data changes.
     *
     * @param listener - The listener to add.
     */
    addListener(listener) {
        if (this.listeners.has(listener)) {
            throw new Error(`[DataContext]: This listener was already added.`);
        }
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }
    /**
     * Gets the value of one of the entries.
     *
     * @param key - The name of the entry to get.
     */
    get(key) {
        const value = this.data[key];
        if (value === undefined) {
            return this.parent?.get(key);
        }
        return value;
    }
    /**
     * Sets new data over the current data.
     * Entries with different names are maintained.
     * Entries with the same name get overwritten.
     *
     * @param data - The new data to set.
     */
    set(data) {
        Object.assign(this.data, data);
        this.fireListeners();
    }
    /**
     * Generates a list of all the entry keys that can be found within the DataContext tree.
     */
    keys() {
        const keys = Object.keys(this.data);
        const parentKeys = this.parent?.keys() ?? [];
        return [...parentKeys, ...keys];
    }
    /**
     * Generates a simple object with all the entries that can be found within the DataContext tree.
     */
    getBulkData() {
        const data = this.data;
        const parentData = this.parent?.getBulkData() ?? {};
        return {
            ...parentData,
            ...data,
        };
    }
    /**
     * Cleans up pending listeners.
     */
    destroy() {
        this.unlisten();
    }
}
DataContext.Context = createContext({});
export { DataContext, };
//# sourceMappingURL=data-context.js.map